import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { EnhancedMarketId, EnhancedReferenceTruck } from '#api/enhancements/enhancedTmsApiTypes'
import type { LocalizedString, ManCodeId, UpdateReferenceTruckRequest } from '#api/tmsApiService'
import type { SerializedError } from '#features/ErrorHandling/errorHelpers'
import type { Brand } from '#types/Brand'

export type ReferenceTruckId = Brand<string, '__ReferenceTruckId'>

export interface CreateOrUpdateReferenceTruckPayload extends UpdateReferenceTruckRequest {
  ReferenceTruckFlag: 'draft' | 'published' | 'archived'
  MANCodeId: ManCodeId
  customTitle?: LocalizedString
  customText?: LocalizedString
}

export interface ReferenceTrucksMarketState {
  referenceTrucks?: EnhancedReferenceTruck[]
  // the ids of the entries that are currently being worked on
  pendingTasks: ReferenceTruckId[]
  status: 'initial' | 'loading' | 'loaded' | 'failed'
  error?: SerializedError
}

export interface ReferenceTrucksState {
  byMarket: Record<EnhancedMarketId, ReferenceTrucksMarketState | undefined>
}

const initialState: ReferenceTrucksState = {
  byMarket: {},
}

export const referenceTrucksSlice = createSlice({
  name: 'referenceTrucks',
  initialState,
  reducers: {
    _setReferenceTrucks: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        referenceTrucks: EnhancedReferenceTruck[]
      }>,
    ) => {
      const { marketId, referenceTrucks } = action.payload
      const entry = state.byMarket[marketId]
      if (!entry) {
        state.byMarket[marketId] = { status: 'loaded', pendingTasks: [], referenceTrucks }
        return
      }
      entry.referenceTrucks = referenceTrucks
    },
    _setMarketStatus: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        status: 'initial' | 'loading' | 'loaded' | 'failed'
        error?: SerializedError
      }>,
    ) => {
      const { marketId, status, error } = action.payload
      const entry = state.byMarket[marketId]
      if (!entry) {
        state.byMarket[marketId] = { status, error, pendingTasks: [] }
        return
      }
      entry.status = status
      entry.error = error
    },
    _pushOrReplaceReferenceTruck: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        referenceTruckId: ReferenceTruckId
        referenceTruck: EnhancedReferenceTruck | null
      }>,
    ) => {
      const { marketId, referenceTruck, referenceTruckId } = action.payload
      let marketState = state.byMarket[marketId]
      if (!marketState) {
        marketState = {
          status: 'loaded',
          pendingTasks: [],
          referenceTrucks: [],
        }
        state.byMarket[marketId] = marketState
      }
      if (!marketState.referenceTrucks) {
        marketState.referenceTrucks = []
      }

      if (!referenceTruck) {
        marketState.referenceTrucks = marketState.referenceTrucks.filter((truck) => truck.referenceTruckId !== referenceTruckId)
        return
      }

      const index = marketState.referenceTrucks.findIndex((truck) => truck.referenceTruckId === referenceTruckId)
      if (index !== -1) {
        marketState.referenceTrucks[index] = referenceTruck
      } else {
        marketState.referenceTrucks.push(referenceTruck)
      }
    },
    loadReferenceTrucksForMarket: (_state, _action: PayloadAction<{ marketId: EnhancedMarketId; forceReload?: boolean }>) => {
      // executed in the middleware
    },
    _addPendingTask: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        taskId: ReferenceTruckId
      }>,
    ) => {
      const marketState = state.byMarket[action.payload.marketId]
      if (!marketState) {
        return
      }
      marketState.pendingTasks.push(action.payload.taskId)
    },
    _removePendingTask: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        taskId: ReferenceTruckId
      }>,
    ) => {
      const marketState = state.byMarket[action.payload.marketId]
      if (!marketState) {
        return
      }
      const index = marketState.pendingTasks.indexOf(action.payload.taskId)
      if (index !== -1) {
        marketState.pendingTasks.splice(index, 1)
      }
    },
  },
})

export const {
  loadReferenceTrucksForMarket,
  _pushOrReplaceReferenceTruck,
  _setMarketStatus,
  _setReferenceTrucks,
  _addPendingTask,
  _removePendingTask,
} = referenceTrucksSlice.actions

export const referenceTrucksActions = referenceTrucksSlice.actions
